/*
  Keyboard + screen-reader behaviour shared by every header disclosure
  (dropdown-menu, mega-menu, mega-menu-sidebar).

  These menus opened on `mouseenter` and, latterly, on `focusin`. Neither
  told assistive tech that a panel existed or what state it was in, nothing
  closed the panel from the keyboard, and the sidebar mega menu — the "Shop"
  menu — kept its category triggers in <span>s, so the panels behind them
  could not be reached at all (accessibility audit F-01, WCAG 2.1.1 / 4.1.2).

  DropdownMenu and MegaMenu are separate classes with no common ancestor, so
  the behaviour is mixed into both prototypes rather than duplicated.
  MegaMenuSidebar picks it up by extending MegaMenu.
*/
const menuDisclosureBehaviour = {
  get topLink() {
    return this.querySelector('.header__menu-item--top');
  },

  // Mirror the visual open state onto the trigger, so a screen reader reports
  // "collapsed"/"expanded" rather than announcing a bare link.
  setExpanded(expanded) {
    const trigger = this.topLink;
    if (trigger && trigger.hasAttribute('aria-controls')) {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  },

  // First thing worth landing on inside the open panel.
  firstPanelItem() {
    const panel = this.querySelector('.list-menu-dropdown');
    if (!panel) return null;
    return panel.querySelector('a[href], button:enabled, [tabindex]:not([tabindex^="-"])');
  },

  openFromKeyboard() {
    this.openPanel();
  },

  // True only for a trigger that actually controls a panel. Top-level links
  // with no submenu carry no aria-controls and must keep behaving as links.
  isDisclosureTrigger(el) {
    return !!el && el === this.topLink && el.hasAttribute('aria-controls');
  },

  /*
    Make the trigger usable by a screen reader.

    A screen reader activates a control by dispatching a click, not by sending
    the key press to the element -- in browse mode it intercepts the keyboard
    entirely. So a menu wired only to keydown and mouseenter opened for sighted
    keyboard users and for the mouse, and stayed shut for exactly the people the
    finding was about.

    `role="button"` is applied here rather than in the Liquid so that, with
    JavaScript unavailable, the element degrades to the plain link it still is
    and "Shop" continues to reach the catalogue.
  */
  initDisclosureTrigger() {
    const trigger = this.topLink;
    if (!trigger || !trigger.hasAttribute('aria-controls')) return;

    trigger.setAttribute('role', 'button');

    trigger.addEventListener('click', (event) => {
      /*
        Only handle activations with no pointer behind them.

        A real click reports detail >= 1; a screen reader's activation, and
        element.click(), report 0. Pointer clicks are left alone deliberately:
        on the desktop the panel is already open from hover by the time a mouse
        reaches the trigger, so toggling here would read as "I clicked Shop and
        it closed".
      */
      if (event.detail !== 0) return;

      event.preventDefault();
      this.classList.contains(this.classes.active) ? this.closeFromKeyboard() : this.openFromKeyboard();
    });
  },

  closeFromKeyboard(returnFocus = true) {
    if (!this.classList.contains(this.classes.active)) return;

    this.close();

    /*
      Returning focus to the trigger would immediately re-open the panel,
      because focus is one of the things that opens it — so Escape appeared to
      do nothing at all (retest, F-01). Suppress the focus-to-open path for the
      moment it takes to hand focus back.
    */
    if (returnFocus && this.topLink) {
      this._suppressFocusOpen = true;
      this.topLink.focus();
      setTimeout(() => {
        this._suppressFocusOpen = false;
      }, 300);
    }
  },

  /*
    Close once focus has genuinely left this menu.

    focusout fires before focus lands on the next element, so activeElement is
    still the outgoing node (or <body>) at this point — the check has to run
    after the browser has moved on. Moving between two items of the same menu
    must not close it, which is why we test containment rather than just
    reacting to the event.

    This deliberately closes when focus reaches a *sibling* nav item too. Tabbing
    off the end of the panel used to leave it hanging open, covering the page,
    while focus sat on the next header link. The earlier exemption for siblings
    existed because focus used to open a menu, and closing from here stripped the
    header state the sibling's delayed open depended on. Menus now open only on
    hover or explicit activation, so there is nothing left to protect.
  */
  handleFocusOut() {
    clearTimeout(this._focusOutTimeout);
    this._focusOutTimeout = setTimeout(() => {
      if (this.contains(document.activeElement)) return;
      this.close();
    }, 0);
  },

  handleKeydown(event) {
    const ARROW_UP = ['ArrowUp', 'Up'];
    const ARROW_DOWN = ['ArrowDown', 'Down'];
    const ARROW_LEFT = ['ArrowLeft', 'Left'];
    const ARROW_RIGHT = ['ArrowRight', 'Right'];
    const activeElement = document.activeElement;

    // Escape closes the panel and hands focus back to the trigger, from
    // anywhere inside the menu (WCAG 2.1.2 — no keyboard trap).
    if (event.key === 'Escape' || event.key === 'Esc') {
      if (!this.classList.contains(this.classes.active)) return;
      event.preventDefault();
      event.stopPropagation();
      this.closeFromKeyboard();
      return;
    }

    // On the trigger itself: Enter and Space toggle the panel, ArrowDown opens
    // it and steps inside. The trigger is an <a>, so navigation has to be
    // suppressed for Enter or the browser follows the href instead.
    if (this.isDisclosureTrigger(activeElement)) {
      if (ARROW_DOWN.includes(event.key)) {
        event.preventDefault();
        this.openFromKeyboard();
        // The panel animates in; wait a frame so the target is focusable.
        requestAnimationFrame(() => {
          const first = this.firstPanelItem();
          if (first) first.focus();
        });
        return;
      }

      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        this.classList.contains(this.classes.active) ? this.closeFromKeyboard() : this.openFromKeyboard();
        return;
      }
    }

    const items = Array.from(this.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])'));
    const currentIndex = items.indexOf(activeElement);
    if (currentIndex === -1) return;

    if (ARROW_DOWN.includes(event.key)) {
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % items.length;
      items[nextIndex].focus();
    } else if (ARROW_UP.includes(event.key)) {
      event.preventDefault();
      const prevIndex = (currentIndex - 1 + items.length) % items.length;
      items[prevIndex].focus();
    } else if (ARROW_RIGHT.includes(event.key)) {
      // Try to move right to the next column if possible
      event.preventDefault();
      const nextColumn = activeElement.closest('.mega-menu__item, .list-menu-dropdown')?.nextElementSibling;
      if (nextColumn) {
        const nextFocusable = nextColumn.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
        if (nextFocusable) nextFocusable.focus();
      }
    } else if (ARROW_LEFT.includes(event.key)) {
      // Try to move left to the previous column if possible
      event.preventDefault();
      const prevColumn = activeElement.closest('.mega-menu__item, .list-menu-dropdown')?.previousElementSibling;
      if (prevColumn) {
        const prevFocusable = prevColumn.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
        if (prevFocusable) prevFocusable.focus();
      }
    }
  },
};

class DropdownMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };
    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      topMenuItem: '.header__menu-item--top',
      menus: 'dropdown-menu, mega-menu, mega-menu-sidebar',
      wrapper: '.list-menu',
      childAnimate: '.list-menu-dropdown > .list-menu'
    };
    this.timeout;

    this.addEventListener('mouseenter', this.handleMouseover.bind(this));
    this.addEventListener('keydown', this.handleKeydown.bind(this));
    this.addEventListener('focusout', this.handleFocusOut.bind(this));
    this.initDisclosureTrigger();
  }

  handleMouseover() {
    if (this.classList.contains(this.classes.active)) return;

    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.resetBackground();
    this.animateMenu();
  }

  handleMouseleave() {
    if (!this.header) return;

    this.reset();
    this.resetBackground();

    this.header.classList.remove(this.classes.hover);
    this.wrapper.removeEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
  }

  bindEvent() {
    this.onHeaderMouseLeaveEvent = this.onHeaderMouseLeaveEvent || this.handleMouseleave.bind(this);

    this.header = this.header || document.querySelector(this.selectors.header);

    this.wrapper = this.closest(this.selectors.wrapper)
    this.wrapper.addEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
  }

  addClasses() {
    const isTopLevel = this.querySelector(this.selectors.topMenuItem);

    let delay = 0;
    if (isTopLevel && !this.isHover()) {
      delay = 150;

      if (this.closest('.header--top-center') || this.closest('.header--top-left')) {
        delay = 200;
      }
    }
    this.timeout = setTimeout(() => {
      if (this.isHover()) {
        this.classList.add(this.classes.active);
        this.setExpanded(true);
      }
    }, delay);

    this.header.classList.add(this.classes.hover);
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);

    menus.forEach((menu) => {
      let found = false;
      const childMenus = menu.querySelectorAll('dropdown-menu');
      childMenus.forEach((element) => {
        if (element === this) {
          found = true;
          return;
        }
      });

      if (!found) menu.reset();
    });

    this.header.classList.remove('has-megamenu-open');
  }

  isHover() {
    return this.header.classList.contains(this.classes.hover);
  }

  reset() {
    this.classList.remove(this.classes.active);
    this.setExpanded(false);

    const childAnimate =  this.querySelector(this.selectors.childAnimate);
    if (childAnimate) childAnimate.classList.remove(this.classes.animate);

    clearTimeout(this.timeout);
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    background.classList.remove(this.classes.active);
  }

  animateMenu() {
    const childAnimate =  this.querySelector(this.selectors.childAnimate);
    if (childAnimate) {
      setTimeout(() => {
        childAnimate.classList.add(this.classes.animate);
      }, 150);
    }
  }

  close() {
    this.handleMouseleave();
  }

  /*
    Open the panel.

    This used to run off `focusin`, so simply tabbing on to the trigger threw
    the whole panel open — a keyboard user passing through the header got a
    full-width menu they never asked for, and could not tab past it. A
    disclosure opens on activation, so this is now only reached from Enter,
    Space or ArrowDown (see menuDisclosureBehaviour.handleKeydown). Hover still
    opens it for pointer users, unchanged.
  */
  openPanel() {
    if (this.classList.contains(this.classes.active)) return;
    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.resetBackground();
    this.animateMenu();
  }
}
// Copy descriptors, not values: `topLink` is a getter, and Object.assign would
// invoke it against the plain mixin object (no querySelector) and throw here,
// aborting the file before any customElements.define ran.
Object.defineProperties(DropdownMenu.prototype, Object.getOwnPropertyDescriptors(menuDisclosureBehaviour));
customElements.define('dropdown-menu', DropdownMenu);

class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };
    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      dropdown: '.list-menu-dropdown',
      menus: 'dropdown-menu, mega-menu, mega-menu-sidebar',
      wrapper: '.list-menu',
      childAnimate: '.mega-menu__item, .mega-menu__promo'
    };
    this.timeout = [];

    this.addEventListener('mouseenter', this.handleMouseover.bind(this));
    this.addEventListener('keydown', this.handleKeydown.bind(this));
    this.addEventListener('focusout', this.handleFocusOut.bind(this));

    // Prevent top-level mega menu link from navigating on click
    const topLink = this.querySelector('.header__menu-item--top');
    if (topLink) {
      topLink.addEventListener('click', (e) => e.preventDefault());
    }

    this.initDisclosureTrigger();
  }

  handleMouseover() {
    if (this.classList.contains(this.classes.active)) return;
    
    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.updateHeaderHeight();
    this.showSublist();
  }

  handleMouseleave() {
    if (!this.header) return;

    this.reset();
    this.resetBackground();

    this.header.classList.remove(this.classes.hover);
    this.header.classList.remove('has-megamenu-open');
    this.wrapper.removeEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
  }

  handleTouchMoveOutside(ev) {
    if (!this.header) return;
    
    let target_section = ev.targetTouches[0].target.closest(this.selectors.header);
    if(!target_section || this.header.classList.contains('shopify-section-header-hidden')) {
      this.reset();
      this.resetBackground();
      
      this.header.classList.remove(this.classes.hover);
      this.header.classList.remove('has-megamenu-open');
      document.removeEventListener('touchmove', this.onHeaderTouchMoveOutsideEvent);
    }
  }

  bindEvent() {
    this.onHeaderMouseLeaveEvent = this.onHeaderMouseLeaveEvent || this.handleMouseleave.bind(this);
    this.onHeaderTouchMoveOutsideEvent = this.onHeaderTouchMoveOutsideEvent || this.handleTouchMoveOutside.bind(this);
    
    this.header = this.header || document.querySelector(this.selectors.header);

    this.wrapper = this.closest(this.selectors.wrapper)
    this.wrapper.addEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
    document.addEventListener('touchmove', this.onHeaderTouchMoveOutsideEvent);
  }

  showSublist() {
    const dropdown = this.querySelector(this.selectors.dropdown);
    const background = this.header.querySelector(this.selectors.background);
    const headerNav = this.header.querySelector('.header');

    const updateSize = () => {
      background.style.setProperty('--height', this.header.clientHeight + dropdown.clientHeight + 'px');
      background.style.setProperty('--dropdown-height', dropdown.clientHeight + 'px');
      background.style.setProperty('--header-nav-height', headerNav.clientHeight + 'px');
    };
    updateSize();
    background.classList.add(this.classes.active);

    // Keep background in sync on resize
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
    this._resizeHandler = () => {
      if (this.classList.contains(this.classes.active)) updateSize();
    };
    window.addEventListener('resize', this._resizeHandler);
  }

  addClasses() {
    this.classList.add(this.classes.active);
    this.setExpanded(true);

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    const delay = childAnimate.length > 5 ? 75 : 150;
    childAnimate.forEach((element, index) => {
      const timeout = setTimeout(() => {
        element.classList.add(this.classes.animate);
      }, (delay * index) + 100);
      
      this.timeout.push(timeout);
    });
    
    this.header.classList.add(this.classes.hover);
    this.header.classList.add('has-megamenu-open');
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);
    menus.forEach((menu) => {
      menu.reset();
    });
    this.header.classList.remove('has-megamenu-open');
  }

  reset() {
    this.classList.remove(this.classes.active);
    this.setExpanded(false);

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    childAnimate.forEach((element) => {
      element.classList.remove(this.classes.animate);
    });
    this.timeout.forEach((timeout) => {
      clearTimeout(timeout);
    });
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    background.classList.remove(this.classes.active);
  }

  close() {
    this.handleMouseleave();
  }

  // See DropdownMenu.openPanel — opened by activation, not by focus.
  openPanel() {
    if (this.classList.contains(this.classes.active)) return;
    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.updateHeaderHeight();
    this.showSublist();
  }

  updateHeaderHeight() {
    const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
    let totalHeaderHeight = 0;
    
    headerGroups.forEach(headerGroup => {
      totalHeaderHeight += headerGroup.offsetHeight;
    });
    
    if (totalHeaderHeight > 0) {
      document.documentElement.style.setProperty('--header-height', `${totalHeaderHeight}px`);
    }
  }
}
Object.defineProperties(MegaMenu.prototype, Object.getOwnPropertyDescriptors(menuDisclosureBehaviour));
customElements.define('mega-menu', MegaMenu);

class MegaMenuSidebar extends MegaMenu {
  constructor() {
    super();

    this.selectors = {
      ...this.selectors,
      firstLinks: '.mega-menu-sidebar__first-link',
      panels: '.mega-menu-sidebar__panel',
      promos: '.mega-menu-sidebar__promo',
    };
  }

  connectedCallback() {
    this.firstLinks = this.querySelectorAll(this.selectors.firstLinks);
    this.panels = this.querySelectorAll(this.selectors.panels);
    this.promos = this.querySelectorAll(this.selectors.promos);

    /*
      A first-level item is a real link: activating it navigates. Its panel is
      revealed on hover for pointer users and on focus for keyboard users,
      rather than by clicking, which would have to swallow the navigation.
    */
    this.firstLinks.forEach((link) => {
      link.addEventListener('mouseenter', this.handleFirstLinkActivate.bind(this));
      link.addEventListener('focusin', this.handleFirstLinkActivate.bind(this));
    });
  }

  handleFirstLinkActivate(event) {
    if (this._suppressFocusOpen && event.type === 'focusin') return;
    const link = event.currentTarget;
    const index = link.dataset.sidebarIndex;

    // Expand to show panels
    this.classList.add('is-expanded');

    // Activate the hovered first-level link
    this.firstLinks.forEach((l) => {
      l.classList.remove('is-active');
      // Only the panel-opening triggers carry aria-controls; plain links don't.
      if (l.hasAttribute('aria-controls')) l.setAttribute('aria-expanded', 'false');
    });
    link.classList.add('is-active');
    if (link.hasAttribute('aria-controls')) link.setAttribute('aria-expanded', 'true');

    // Show matching panel
    this.panels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.sidebarPanel === index);
    });

    // Show matching promo image
    this.promos.forEach((promo) => {
      promo.classList.toggle('is-active', promo.dataset.sidebarPromo === index);
    });

    // Recalculate background after panel is visible. layoutColumns must run
    // first — it decides the panel's column count, and so its width, which
    // showSublist reads to size the header background.
    requestAnimationFrame(() => {
      this.layoutColumns();
      this.showSublist();
    });
  }

  /*
    Set the column count on the visible panel so every group fits.

    The panel used to be a wrapping column-flex container, but Safari doesn't
    grow such a container's width to fit the columns its content wraps into, so
    the extra columns fell outside the shrink-to-fit dropdown and were clipped.
    It's CSS multi-column now, which both engines size the same — but
    multi-column needs to be told how many columns to make: `column-width` needs
    a definite width to divide, and the dropdown is shrink-to-fit, so there
    isn't one. A hardcoded count would reserve an empty column for short menus.

    Don't try to predict the count. Two earlier attempts did and both dropped
    groups off the end on short windows: dividing total height by column height
    ignores the gaps `break-inside: avoid` leaves when a tall group jumps to the
    next column, and even simulating that packing is wrong because group heights
    are measured at one column count but the links can reflow at another.

    So apply a count, ask the browser whether anything still overflows, and grow
    until nothing does. The browser is the only reliable authority on where its
    own column breaks land.

    Panels are display:none until active, which measures as zero height, so this
    only runs on the active panel, after it has been made visible.
  */
  layoutColumns() {
    const panel = this.querySelector('.mega-menu-sidebar__panel.is-active');
    const column = panel && panel.querySelector('.mega-menu-sidebar__panel-column');
    if (!column) return;

    const groups = Array.from(column.children);
    if (!groups.length) return;

    // One column per group is the most that could ever be needed, so the loop
    // always terminates.
    for (let count = 1; count <= groups.length; count += 1) {
      column.style.columnCount = count;

      // Reading a layout property forces the reflow, so the rects below reflect
      // this count rather than the previous one.
      const box = column.getBoundingClientRect();

      const overflows = groups.some((group) => {
        const rect = group.getBoundingClientRect();
        return rect.bottom > box.bottom + 1 || rect.right > box.right + 1;
      });

      if (!overflows) return;
    }
  }

  /*
    In the sidebar layout the category triggers are in a left-hand column and
    their content is in a panel to the right, so ArrowRight should cross into
    the panel. The inherited handler looks for a sibling column instead, which
    this layout doesn't have — handle it here and defer everything else.
  */
  handleKeydown(event) {
    const isRight = event.key === 'ArrowRight' || event.key === 'Right';
    const trigger = document.activeElement;

    if (isRight && trigger && trigger.classList.contains('mega-menu-sidebar__first-link')) {
      const panel = this.querySelector('.mega-menu-sidebar__panel.is-active');
      const first = panel && panel.querySelector('a[href], button:enabled');
      if (first) {
        event.preventDefault();
        first.focus();
        return;
      }
    }

    // ArrowLeft from inside a panel goes back to the trigger that opened it.
    if ((event.key === 'ArrowLeft' || event.key === 'Left') && trigger && trigger.closest('.mega-menu-sidebar__panel')) {
      const active = this.querySelector('.mega-menu-sidebar__first-link.is-active');
      if (active) {
        event.preventDefault();
        active.focus();
        return;
      }
    }

    menuDisclosureBehaviour.handleKeydown.call(this, event);
  }

  showSublist() {
    const dropdown = this.querySelector(this.selectors.dropdown);
    const background = this.header.querySelector(this.selectors.background);
    const headerNav = this.header.querySelector('.header');

    const dropdownWidth = dropdown.scrollWidth;
    background.style.setProperty('--height', this.header.clientHeight + dropdown.clientHeight + 'px');
    background.style.setProperty('--dropdown-height', dropdown.clientHeight + 'px');
    background.style.setProperty('--header-nav-height', headerNav.clientHeight + 'px');
    background.style.width = dropdownWidth + 'px';
    background.classList.add(this.classes.active);
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    background.classList.remove(this.classes.active);
    // Keep width during close transition, then clear it
    background.addEventListener('transitionend', () => {
      if (!background.classList.contains(this.classes.active)) {
        background.style.width = '';
      }
    }, { once: true });
  }

  reset() {
    super.reset();
    this.classList.remove('is-expanded');

    // Clear active states so it reopens collapsed
    if (this.firstLinks) {
      this.firstLinks.forEach((l) => {
        l.classList.remove('is-active');
        if (l.hasAttribute('aria-controls')) l.setAttribute('aria-expanded', 'false');
      });
    }
    if (this.panels) {
      this.panels.forEach((p) => p.classList.remove('is-active'));
    }
    if (this.promos) {
      this.promos.forEach((p) => p.classList.remove('is-active'));
    }
  }
}
customElements.define('mega-menu-sidebar', MegaMenuSidebar);

class SidebarMobileSplit extends HTMLElement {
  connectedCallback() {
    this.triggers = this.querySelectorAll('[data-sidebar-split-index]');
    this.panels = this.querySelectorAll('[data-sidebar-split-panel]');
    this.promos = this.querySelectorAll('[data-sidebar-split-promo]');

    this.triggers.forEach((btn) => {
      btn.addEventListener('click', this.expand.bind(this));
    });
  }

  expand(event) {
    const index = event.currentTarget.dataset.sidebarSplitIndex;

    this.triggers.forEach((t) => t.classList.remove('is-active'));
    event.currentTarget.classList.add('is-active');

    this.panels.forEach((p) => {
      p.classList.toggle('is-active', p.dataset.sidebarSplitPanel === index);
    });

    this.promos.forEach((p) => {
      p.classList.toggle('is-active', p.dataset.sidebarSplitPromo === index);
    });

    this.classList.add('is-expanded');
  }
}
customElements.define('sidebar-mobile-split', SidebarMobileSplit);
